<script setup>
import { computed } from 'vue'

/**
 * The component's inherited properties.
 */
const props = defineProps({
    /**
     * The grid column's extra param definition.
     *
     * @type {Object}
     */
    extraParams: {
        default() {
            return {}
        },
    },

    /**
     * The given load.
     *
     * @type {Object}
     */
    load: {
        type: Object,
        required: true,
    },

    /**
     * The current location of the load.
     *
     * @type {Object}
     */
    value: {
        type: Object,
        required: true,
    },
})

/**
 * The arrived stops of the load.
 *
 * @return {Number}
 */
const arrivedStops = computed(() => {
    return props.load.summary?.stops?.arrived || 0
})

/**
 * Determine if the current location has a valid name.
 *
 * @return {Boolean}
 */
const isValidName = computed(() => {
    const name = props.value?.name

    if (!name) {
        return false
    }

    return `${name}`.toUpperCase() !== 'UNKNOWN'
})

/**
 * The stop name.
 *
 * @return {String}
 */
const stopName = computed(() => {
    const name = props.value?.name ? `${props.value?.name}` : ''

    if (name.length > 30) {
        return name.substring(0, 28) + '...'
    }

    return name
})

/**
 * The stop title.
 *
 * @return {String}
 */
const title = computed(() => {
    return `${props.value?.name} - ${props.value?.city}, ${props.value?.state}`
})

/**
 * The total stops of the load.
 *
 * @return {Number}
 */
const totalStops = computed(() => {
    return props.load.summary?.stops?.total || 0
})
</script>

<template>
    <div class="flex items-center">
        <span v-if="isValidName" data-test="stop-basic-name" :title="title">
            {{ stopName }}
            - {{ value.city }}, {{ value.state }}
        </span>
        <span v-else data-test="stop-basic-name" :title="title"> Unknown </span>
        <v-chip
            v-if="value?.type || extraParams.stop === 'current_stop'"
            color="primary"
            class="ms-2"
            size="x-small"
            data-test="stop-basic-chip"
        >
            <template v-if="extraParams.stop === 'current_stop'">
                Stop {{ arrivedStops }} of {{ totalStops }} ·
            </template>
            {{ value.type }}
        </v-chip>
    </div>
</template>
